import { useState, useEffect } from 'react';
import { Button } from '../../../../components/Button';
import { MaterialIcon } from '@/components/MaterialIcon';
import Checkbox from '../../../../components/Checkbox';
import Select from '../../../../components/Select';
import { Switch } from '@/components/ui/switch';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import useFetchOrganization from '../api/useFetchOrganization';
import useUpdateOrganizationNotification from '../api/useUpdateOrganizationNotification';
import useDeleteOrganizationNotification from '../api/useDeleteOrganizationNotification';
import type { Configuration, ConfigurationRequest, JobPostSettings } from '../types/Organization';
import NotificationSettingsModal from '../components/NotificationSettingsModal';
import usePostOrganizationNotification from '../api/usePostOrganizationNotification';
import useUpdateOrganization from '../api/useUpdateOrganization';
import { useQueryClient } from '@tanstack/react-query';
import OrganizationSettingsTabs from '../components/SettingsTabs';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';

interface Props {
  initialSettings?: Configuration;
}

interface NotificationTimingSettings {
  isJobPostChanged: boolean;
  isRegisteredForm: boolean;
  isCandidateEndpointAction: boolean;
}

export default function GeneralSettings({ initialSettings }: Props) {
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [settings, setSettings] = useState<Configuration>(
    initialSettings || {
      notify_type: 'slack',
      configuration: {
        webhook_url: null,
        api_token: null,
        room_id: null,
      },
    }
  );
  const { data: organization, refetch: refetchOrganization } = useFetchOrganization();
  const { mutateAsync: updateOrganizationNotification } = useUpdateOrganizationNotification();
  const { mutateAsync: deleteOrganizationNotification } = useDeleteOrganizationNotification();
  const { mutateAsync: postOrganizationNotification } = usePostOrganizationNotification();
  const { mutateAsync: updateOrganization } = useUpdateOrganization();
  const queryClient = useQueryClient();
  const [isAllianceUpdating, setIsAllianceUpdating] = useState(false);

  // 通知のタイミング設定用のstate
  const [timingSettings, setTimingSettings] = useState<NotificationTimingSettings>({
    isJobPostChanged: false,
    isRegisteredForm: false,
    isCandidateEndpointAction: false,
  });

  useEffect(() => {
    if (organization?.notification_setting) {
      setTimingSettings({
        isJobPostChanged: organization.notification_setting.is_job_post_changed ?? false,
        isRegisteredForm: organization.notification_setting.is_registered_form ?? false,
        isCandidateEndpointAction:
          organization.notification_setting.is_candidate_endpoint_action ?? false,
      });
    }
  }, [organization]);

  const handleSyncSettingChange = async (checked: boolean) => {
    try {
      await updateOrganization({
        job_post_settings: {
          is_allow_sync: checked,
          archive_job_post_endpoint_behavior: (organization?.job_post_setting
            ?.archive_job_post_endpoint_behavior ??
            'archive_suggest_endpoints') as JobPostSettings['archive_job_post_endpoint_behavior'],
        },
      });
      await refetchOrganization();
      showSuccessToast('自動更新設定を保存しました。');
    } catch (error) {
      showErrorToast('自動更新設定を保存できませんでした。');
    }
  };

  const syncSettingOptions = [
    {
      value: 'true',
      label: '自動で更新を許可する',
    },
    {
      value: 'false',
      label: '自動で更新しない',
    },
  ];

  const handleSyncSettingSelectChange = async (value: string) => {
    const checked = value === 'true';
    await handleSyncSettingChange(checked);
  };

  const handleArchiveBehaviorChange = async (value: string) => {
    try {
      await updateOrganization({
        job_post_settings: {
          is_allow_sync: organization?.job_post_setting?.is_allow_sync ?? false,
          archive_job_post_endpoint_behavior: value as
            'archive_suggest_endpoints' | 'keep_endpoint_status' | 'archive_all_endpoints',
        },
      });
      await refetchOrganization();
      showSuccessToast('提案の動作設定を保存しました。');
    } catch (error) {
      showErrorToast('提案の動作設定を保存できませんでした。');
    }
  };

  const archiveBehaviorOptions = [
    {
      value: 'archive_suggest_endpoints',
      label: '提案中のみアーカイブ',
    },
    {
      value: 'keep_endpoint_status',
      label: '提案のステータスを維持',
    },
    {
      value: 'archive_all_endpoints',
      label: 'すべての提案をアーカイブ',
    },
  ];

  const handleNotificationSettingsSave = async (notificationSettings: ConfigurationRequest) => {
    try {
      // 新規登録の場合はPOST、編集の場合はPUT
      if (organization?.notification_setting?.configuration) {
        await updateOrganizationNotification(notificationSettings);
      } else {
        await postOrganizationNotification(notificationSettings);
      }
      await refetchOrganization();
      setIsNotificationModalOpen(false);
      showSuccessToast('通知設定を保存しました。');
    } catch (error) {
      showErrorToast('通知設定を保存できませんでした。');
    }
  };

  const handleTimingSettingChange = async (
    key: keyof NotificationTimingSettings,
    checked: boolean
  ) => {
    const newSettings = {
      ...timingSettings,
      [key]: checked,
    };

    // 一時的な状態を更新
    setTimingSettings(newSettings);

    try {
      if (!organization?.notification_setting?.configuration) {
        showErrorToast('通知設定を保存できませんでした。');
        return;
      }

      await updateOrganization({
        notification_settings: {
          is_job_post_changed:
            key === 'isJobPostChanged' ? checked : timingSettings.isJobPostChanged,
          is_registered_form:
            key === 'isRegisteredForm' ? checked : timingSettings.isRegisteredForm,
          is_candidate_endpoint_action:
            key === 'isCandidateEndpointAction'
              ? checked
              : timingSettings.isCandidateEndpointAction,
        },
      });
      await refetchOrganization();
      showSuccessToast('通知のタイミング設定を保存しました。');
    } catch (error) {
      showErrorToast('通知設定を保存できませんでした。');
      // エラーが発生した場合は元の状態に戻す
      setTimingSettings((prev) => ({
        ...prev,
        [key]: !checked,
      }));
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteOrganizationNotification();
      await refetchOrganization();
      showSuccessToast('通知先の連携を解除しました。');
    } catch (error) {
      showErrorToast('通知先の連携を解除できませんでした。');
    }
    setIsDeleteConfirmOpen(false);
    // 設定の初期化
    setSettings({
      notify_type: 'slack',
      configuration: {
        webhook_url: null,
        api_token: null,
        room_id: null,
      },
    });
  };

  return (
    <>
      <OrganizationSettingsTabs />
      <div className="mt-4 border-spacing-1 rounded border border-solid border-gray-100 bg-white">
        <div className="px-12 py-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-800">求人票設定</h2>
          <div className="space-y-4">
            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                外部連携の更新した時の動作
              </label>
              <div className="w-full max-w-md">
                <Select
                  items={syncSettingOptions}
                  value={
                    organization?.job_post_setting?.is_allow_sync != null
                      ? String(organization.job_post_setting.is_allow_sync)
                      : 'false'
                  }
                  onValueChange={handleSyncSettingSelectChange}
                  defaultLabel="選択してください"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                外部連携している求人元が更新されたときの求人票の動作を設定します。
              </p>
            </div>
            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                求人終了時の提案の動作
              </label>
              <div className="w-full max-w-md">
                <Select
                  items={archiveBehaviorOptions}
                  value={
                    organization?.job_post_setting?.archive_job_post_endpoint_behavior ||
                    'archive_suggest_endpoints'
                  }
                  onValueChange={handleArchiveBehaviorChange}
                  defaultLabel="選択してください"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                求人終了時に、関連する提案をどのように処理するかを設定します。
              </p>
            </div>
            <div className="mt-md flex flex-col items-start gap-xs">
              <label className="text-heading-xxs">アライアンス機能</label>
              <div className="flex items-center gap-xs">
                <Switch
                  checked={organization?.is_alliance_enabled ?? false}
                  disabled={isAllianceUpdating}
                  onCheckedChange={async (checked: boolean) => {
                    setIsAllianceUpdating(true);
                    try {
                      await updateOrganization({ is_alliance_enabled: checked });
                      await refetchOrganization();
                      // サイドバーのメニュー表示を更新
                      queryClient.invalidateQueries({ queryKey: ['iam'] });
                      showSuccessToast(
                        checked
                          ? 'アライアンス機能を有効にしました'
                          : 'アライアンス機能を無効にしました'
                      );
                    } catch {
                      showErrorToast('アライアンス機能の設定変更に失敗しました');
                    } finally {
                      setIsAllianceUpdating(false);
                    }
                  }}
                />
                <span className="text-sm">使用する</span>
              </div>
              <p className="text-xs text-sub">
                提携先エージェントに求人を共有する機能を使用するか設定します。
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 border-spacing-1 rounded border border-solid border-gray-100 bg-white">
        <div className="px-12 py-6">
          <div className="mb-4 flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-800">通知設定</h2>
            {!organization?.notification_setting?.configuration && (
              <span className="rounded-full bg-danger_lighten px-3 py-1 text-xs font-bold text-danger">
                未設定
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {organization?.notification_setting?.configuration && (
              <span className="text-lg font-medium text-gray-700">
                {organization?.notification_setting?.configuration?.notify_type === 'slack'
                  ? 'Slack'
                  : 'Chatwork'}
              </span>
            )}
            <Button size="sm" color={'primary'} onClick={() => setIsNotificationModalOpen(true)}>
              {/* 編集するのときだけアイコン追加 */}
              {organization?.notification_setting?.configuration ? (
                <div className="flex items-center gap-1">
                  <MaterialIcon name="edit" className="text-base" />
                  {'編集する'}
                </div>
              ) : (
                '通知先を設定する'
              )}
            </Button>
            {organization?.notification_setting?.configuration && (
              <Button size="sm" color="danger" iconOnly onClick={handleDeleteClick} title="削除">
                <MaterialIcon name="delete" className="text-base" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* 通知のタイミングセクション */}
      {organization?.notification_setting?.configuration && (
        <div className="mt-4 border-spacing-1 rounded border border-solid border-gray-100 bg-white">
          <div className="px-12 py-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-800">通知のタイミング</h2>
            <div className="space-y-4">
              <Checkbox
                value="job_post_changed"
                checked={timingSettings.isJobPostChanged}
                onChange={(checked) => handleTimingSettingChange('isJobPostChanged', checked)}
              >
                <span className="text-md font-medium text-gray-700">求人オープン・クローズ</span>
                <span className="ml-2 text-sm text-gray-500">
                  外部サービスで求人が公開・非公開したタイミングで通知します。
                </span>
              </Checkbox>
              <Checkbox
                value="registered_form"
                checked={timingSettings.isRegisteredForm}
                onChange={(checked) => handleTimingSettingChange('isRegisteredForm', checked)}
              >
                <span className="text-md font-medium text-gray-700">登録フォーム送信時</span>
                <span className="ml-2 text-sm text-gray-500">
                  登録フォームで回答を受け取った時に通知します。
                </span>
              </Checkbox>
              <Checkbox
                value="candidate_endpoint_action"
                checked={timingSettings.isCandidateEndpointAction}
                onChange={(checked) =>
                  handleTimingSettingChange('isCandidateEndpointAction', checked)
                }
              >
                <span className="text-md font-medium text-gray-700">マイページ応募・辞退</span>
                <span className="ml-2 text-sm text-gray-500">
                  マイページで求職者のアクション時に通知します。
                </span>
              </Checkbox>
            </div>
          </div>
        </div>
      )}

      <NotificationSettingsModal
        open={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
        onSave={handleNotificationSettingsSave}
        initialSettings={settings}
        organization={organization}
      />

      <DeleteConfirmationDialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        serviceName="Slack"
      />
    </>
  );
}
